import { Box, Button, Chip, IconButton, LinearProgress, Tooltip, Typography } from '@mui/material'
import { FC, Fragment, useState } from 'react'
import { PropertyProps } from '../../types/properties'
import { getImageUrl, numberWithCommas } from '../../helpers'
import {
    DeleteOutlineOutlined,
    EditOutlined,
    HomeWorkOutlined,
    LocationOnOutlined,
    OpenInNewOutlined,
    PeopleOutlined,
    Star,
    TrendingUpOutlined,
    WarningAmberOutlined,
} from '@mui/icons-material'
import { useNavigate } from 'react-router'
import { Connect, mapDispatchToProps, mapStateToProps } from '../../Redux'
import DeleteModal from '../Common/deletePrompt'

const PRIMARY       = '#8c7c0c'
const PRIMARY_LIGHT = '#f5f2e0'
const WARM_GREEN    = '#6a8c0c'
const WARM_AMBER    = '#d4890a'
const WARM_RED      = '#c05c13'

interface StateProps { showDeleteModal: null | PropertyProps }
interface Props {
    item: PropertyProps
    selectedProperty?: PropertyProps
    setRedux?: (data: any) => void
    properties?: PropertyProps[]
}

const PropertyInfo: FC<Props> = ({ item, selectedProperty, properties, setRedux }) => {
    const [state, setState] = useState<StateProps>({ showDeleteModal: null })
    const [imgFailed, setImgFailed] = useState(false)
    const navigate = useNavigate()
    const { showDeleteModal } = state

    const {
        totalUnits, occupiedUnits, placeName, expectedRevenue,
        _id, arrears, currentMonthRevenue, logos, name, location, role
    } = item

    const isSelected    = selectedProperty?._id === _id
    const occupancyPct  = totalUnits > 0 ? Math.round(occupiedUnits * 100 / totalUnits) : 0
    const collectionPct = expectedRevenue > 0 ? Math.round(currentMonthRevenue * 100 / expectedRevenue) : 0
    const occupancyColor = occupancyPct >= 80 ? WARM_GREEN : occupancyPct >= 50 ? WARM_AMBER : WARM_RED
    const logoUrl = getImageUrl(logos, 'small')
    const showFallback = !logoUrl || imgFailed
    const initials = name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2)

    const paymentAccount = (item as any).paymentAccount
    const hasPaymentAccount = Boolean(
        paymentAccount?.active === 'Bank' ? paymentAccount?.Bank?.bankId : paymentAccount?.Phone
    )

    const onSetAsDefault = () => {
        if (setRedux) setRedux({ selectedProperty: { _id, name, logos, role, hasPaymentAccount } })
        navigate('/dashboard')
    }

    const stats = [
        { icon: <HomeWorkOutlined sx={{ fontSize: 14 }} />, label: 'Units',     value: String(totalUnits),                        color: PRIMARY      },
        { icon: <PeopleOutlined   sx={{ fontSize: 14 }} />, label: 'Occupied',  value: `${occupancyPct}%`,                        color: occupancyColor},
        { icon: <TrendingUpOutlined sx={{ fontSize: 14 }} />, label: 'Collected', value: `Ksh ${numberWithCommas(currentMonthRevenue)}`, color: WARM_GREEN    },
        { icon: <WarningAmberOutlined sx={{ fontSize: 14 }} />, label: 'Arrears', value: `Ksh ${numberWithCommas(arrears)}`,       color: arrears > 0 ? WARM_RED : WARM_GREEN },
    ]

    return (
        <Fragment>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 2, md: 2.5 },
                bgcolor: isSelected ? PRIMARY_LIGHT : '#fff',
                borderRadius: 3,
                p: { xs: 2, md: 2 },
                mb: 1.5,
                boxShadow: '0 1px 4px rgba(0,0,0,.06), 0 4px 16px rgba(0,0,0,.04)',
                transition: 'box-shadow .18s, transform .15s',
                '&:hover': { boxShadow: `0 4px 24px rgba(140,124,12,.13)`, transform: 'translateY(-1px)' },
                flexWrap: { xs: 'wrap', lg: 'nowrap' },
            }}>

                {/* ── Logo ── */}
                <Box
                    onClick={onSetAsDefault}
                    sx={{
                        width: { xs: 52, md: 64 },
                        height: { xs: 52, md: 64 },
                        borderRadius: 2,
                        flexShrink: 0,
                        overflow: 'hidden',
                        border: '2px solid rgba(140,124,12,.15)',
                        bgcolor: PRIMARY_LIGHT,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                    }}
                >
                    {!showFallback
                        ? <Box
                            component="img"
                            src={logoUrl}
                            alt={name}
                            onError={() => setImgFailed(true)}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                          />
                        : <Typography sx={{ fontWeight: 800, fontSize: 18, color: PRIMARY, lineHeight: 1, userSelect: 'none' }}>
                            {initials}
                          </Typography>
                    }
                </Box>

                {/* ── Name + location + bar ── */}
                <Box sx={{ flex: { xs: 1, lg: '0 0 220px' }, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a', lineHeight: 1.2 }} noWrap>
                            {name}
                        </Typography>
                        {isSelected && (
                            <Chip
                                icon={<Star sx={{ fontSize: '11px !important', color: `${PRIMARY} !important` }} />}
                                label="Active"
                                size="small"
                                sx={{ bgcolor: PRIMARY_LIGHT, color: PRIMARY, fontWeight: 700, fontSize: 10, height: 20, px: 0.2 }}
                            />
                        )}
                    </Box>
                    {(placeName || location?.locName) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mt: 0.4 }}>
                            <LocationOnOutlined sx={{ fontSize: 11, color: '#94a3b8' }} />
                            <Typography sx={{ fontSize: 11, color: '#94a3b8' }} noWrap>
                                {[placeName, location?.locName].filter(Boolean).join(', ')}
                            </Typography>
                        </Box>
                    )}
                    <LinearProgress
                        variant="determinate"
                        value={occupancyPct}
                        sx={{
                            mt: 1, height: 3, borderRadius: 2, bgcolor: '#f1f5f9',
                            width: { xs: '100%', lg: 140 },
                            '& .MuiLinearProgress-bar': { bgcolor: occupancyColor, borderRadius: 2 },
                        }}
                    />
                </Box>

                {/* ── Divider ── */}
                <Box sx={{ display: { xs: 'none', lg: 'block' }, width: '1px', height: 48, bgcolor: '#f1f5f9', flexShrink: 0 }} />

                {/* ── Stats ── */}
                <Box sx={{
                    display: 'flex',
                    gap: { xs: 2, md: 3 },
                    flex: 1,
                    flexWrap: 'wrap',
                    width: { xs: '100%', lg: 'auto' },
                }}>
                    {stats.map(({ icon, label, value, color }) => (
                        <Box key={label} sx={{ minWidth: 72 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, mb: 0.2 }}>
                                <Box sx={{ color }}>{icon}</Box>
                                <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: .4 }}>
                                    {label}
                                </Typography>
                            </Box>
                            <Typography sx={{ fontSize: 13, fontWeight: 800, color, lineHeight: 1 }}>
                                {value}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* ── Divider ── */}
                <Box sx={{ display: { xs: 'none', lg: 'block' }, width: '1px', height: 48, bgcolor: '#f1f5f9', flexShrink: 0 }} />

                {/* ── Actions ── */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
                    {!isSelected ? (
                        <Button
                            size="small"
                            variant="contained"
                            startIcon={<Star sx={{ fontSize: 13 }} />}
                            onClick={onSetAsDefault}
                            sx={{
                                textTransform: 'none', fontWeight: 700, fontSize: 12,
                                bgcolor: PRIMARY, borderRadius: 2, py: 0.7, px: 1.8,
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: '#6b5e09' },
                            }}
                        >
                            Set Active
                        </Button>
                    ) : (
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<OpenInNewOutlined sx={{ fontSize: 13 }} />}
                            onClick={() => navigate('/dashboard')}
                            sx={{
                                textTransform: 'none', fontWeight: 700, fontSize: 12,
                                borderColor: PRIMARY, color: PRIMARY, borderRadius: 2, py: 0.7, px: 1.8,
                                whiteSpace: 'nowrap',
                                '&:hover': { bgcolor: PRIMARY_LIGHT, borderColor: PRIMARY },
                            }}
                        >
                            Dashboard
                        </Button>
                    )}
                    <Tooltip title="Edit property">
                        <IconButton size="small" onClick={() => navigate('/new_property', { state: { item } })}
                            sx={{ bgcolor: '#f8fafc', borderRadius: 1.5, '&:hover': { bgcolor: PRIMARY_LIGHT } }}>
                            <EditOutlined sx={{ fontSize: 16, color: '#64748b' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete property">
                        <IconButton size="small" onClick={() => setState({ ...state, showDeleteModal: item })}
                            sx={{ bgcolor: '#fdf2ec', borderRadius: 1.5, '&:hover': { bgcolor: '#fde8dd' } }}>
                            <DeleteOutlineOutlined sx={{ fontSize: 16, color: WARM_RED }} />
                        </IconButton>
                    </Tooltip>
                </Box>
            </Box>

            {showDeleteModal && (
                <DeleteModal
                    item={item}
                    close={() => setState({ ...state, showDeleteModal: null })}
                    onDelete={() => {
                        setState({ ...state, showDeleteModal: null })
                        const idx = properties?.findIndex(q => q._id === item._id) as number
                        const updated = [...(properties as PropertyProps[])]
                        updated.splice(idx, 1)
                        if (setRedux)
                            setRedux(isSelected
                                ? { properties: updated, selectedProperty: null }
                                : { properties: updated }
                            )
                    }}
                    page='Property'
                    module={'Properties'}
                    itemName={showDeleteModal.name}
                    propertyId={showDeleteModal._id}
                />
            )}
        </Fragment>
    )
}

export default Connect(mapStateToProps, mapDispatchToProps)(PropertyInfo)
